import React, { useState, useEffect, useCallback } from "react";
import Layout from "../components/Layout";
import { api, formatBRL } from "../lib/api";
import { toast } from "sonner";
import {
  ShieldCheck, ShieldAlert, Loader2, CheckCircle2, XCircle,
  Users, FileText, Briefcase, TrendingUp, AlertTriangle, RefreshCcw,
  Wrench, ScrollText,
} from "lucide-react";

function Section({ title, icon: Icon, children, count, accent = "gold" }) {
  const color = accent === "red"
    ? "border-red-500/30 text-red-300"
    : accent === "green"
    ? "border-emerald-500/30 text-emerald-300"
    : "border-[#D4AF37]/30 text-[#D4AF37]";
  return (
    <section className="fc-card p-5 sm:p-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-display text-[15px] font-semibold tracking-tight flex items-center gap-2">
          {Icon && <Icon size={16} className={color.split(" ")[1]}/>}
          {title}
        </h2>
        {count !== undefined && (
          <span className={`badge-base text-[11px] border ${color} bg-[#0f0f0f]`}>{count}</span>
        )}
      </div>
      {children}
    </section>
  );
}

function FinancialRow({ row }) {
  return (
    <div className={`grid grid-cols-12 gap-2 items-center px-3 py-2.5 rounded-md border ${
      row.ok ? "border-[#1f1f1f] bg-[#0a0a0a]" : "border-red-500/30 bg-red-500/5"
    }`} data-testid={`fin-row-${row.label.replace(/\s+/g, "-").toLowerCase()}`}>
      <div className="col-span-12 sm:col-span-5">
        <div className="text-[13px]">{row.label}</div>
        {row.hint && <div className="text-[10.5px] text-[#737373] mt-0.5">{row.hint}</div>}
      </div>
      <div className="col-span-4 sm:col-span-2">
        <div className="text-[10px] uppercase tracking-wider text-[#737373]">Dashboard</div>
        <div className="font-mono-tab text-[13px]">{formatBRL(row.dashboard)}</div>
      </div>
      <div className="col-span-4 sm:col-span-2">
        <div className="text-[10px] uppercase tracking-wider text-[#737373]">Calculado</div>
        <div className="font-mono-tab text-[13px]">{formatBRL(row.calculated)}</div>
      </div>
      <div className="col-span-2 sm:col-span-2">
        <div className="text-[10px] uppercase tracking-wider text-[#737373]">Diferença</div>
        <div className={`font-mono-tab text-[13px] ${Math.abs(row.diff) < 0.05 ? "text-emerald-300" : "text-red-300"}`}>
          {row.diff > 0 ? "+" : ""}{formatBRL(row.diff)}
        </div>
      </div>
      <div className="col-span-2 sm:col-span-1 flex justify-end">
        {row.ok
          ? <CheckCircle2 size={18} className="text-emerald-400"/>
          : <XCircle size={18} className="text-red-400"/>}
      </div>
    </div>
  );
}

const ISSUE_LABEL = {
  sem_cliente: "Sem cliente vinculado",
  sem_indicador: "Sem indicador",
  sem_socio: "Sem sócio",
  sem_valor: "Sem valor",
  saldo_negativo: "Saldo negativo",
  duplicado: "Contrato duplicado",
  socio_invalido: "Sócio inválido",
  sem_cpf: "Sem CPF",
  sem_documentos: "Sem documentos",
  cpf_duplicado: "CPF duplicado",
};

export default function AuditoriaPage() {
  const [loading, setLoading] = useState(false);
  const [report, setReport] = useState(null);
  const [cpfReport, setCpfReport] = useState(null);
  const [loadingCpf, setLoadingCpf] = useState(false);

  // Novas seções auto-carregadas
  const [orphans, setOrphans] = useState(null); // { total, items }
  const [loadingOrphans, setLoadingOrphans] = useState(false);
  const [fixingId, setFixingId] = useState(null); // contract_id em correção
  const [fixingAll, setFixingAll] = useState(false);
  const [regErrors, setRegErrors] = useState(null); // { total, items, by_stage_last_7d }
  const [loadingRegErrors, setLoadingRegErrors] = useState(false);

  // Contratos sem sócio vinculado (legado)
  const [unlinked, setUnlinked] = useState(null); // { total, items }
  const [loadingUnlinked, setLoadingUnlinked] = useState(false);
  const [linkingId, setLinkingId] = useState(null);
  const [linkingAll, setLinkingAll] = useState(false);
  const [socioOptions, setSocioOptions] = useState([]);
  const [linkChoice, setLinkChoice] = useState({}); // { [contract_id]: socio_id }
  // Parcelados sem sócio vinculado
  const [unlinkedIc, setUnlinkedIc] = useState(null);
  const [loadingUnlinkedIc, setLoadingUnlinkedIc] = useState(false);
  const [linkingIcId, setLinkingIcId] = useState(null);
  const [linkChoiceIc, setLinkChoiceIc] = useState({});

  const loadUnlinked = useCallback(async () => {
    setLoadingUnlinked(true);
    try {
      const { data } = await api.get("/audit/contracts-without-socio");
      setUnlinked(data);
      // pré-seleciona o sócio sugerido em cada linha
      const seed = {};
      for (const it of data.items || []) {
        if (it.suggested_socio_id) seed[it.contract_id] = it.suggested_socio_id;
      }
      setLinkChoice((prev) => ({ ...seed, ...prev }));
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Falha ao listar contratos sem sócio");
    } finally {
      setLoadingUnlinked(false);
    }
  }, []);

  // Diagnóstico bruto unificado (Contratos + Parcelados + Acordos)
  const [rawDiag, setRawDiag] = useState(null);
  const [loadingRawDiag, setLoadingRawDiag] = useState(false);
  const [rawTipo, setRawTipo] = useState("");           // "" | contrato | parcelado | acordo
  const [rawSocioName, setRawSocioName] = useState("");
  const [rawClientName, setRawClientName] = useState("");
  const [rawOnlyExcluded, setRawOnlyExcluded] = useState(false);
  const [rawOnlyUnlinked, setRawOnlyUnlinked] = useState(false);

  // Diagnóstico detalhado de exposição por sócio
  const [socioOptionsAll, setSocioOptionsAll] = useState([]);
  const [selectedSocioId, setSelectedSocioId] = useState("");
  const [breakdown, setBreakdown] = useState(null);
  const [loadingBreakdown, setLoadingBreakdown] = useState(false);

  const loadUnlinkedIc = useCallback(async () => {
    setLoadingUnlinkedIc(true);
    try {
      const { data } = await api.get("/audit/installment-contracts-without-socio");
      setUnlinkedIc(data);
      const seed = {};
      for (const it of data.items || []) {
        if (it.suggested_socio_id) seed[it.installment_contract_id] = it.suggested_socio_id;
      }
      setLinkChoiceIc((prev) => ({ ...seed, ...prev }));
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Falha ao listar parcelados sem sócio");
    } finally {
      setLoadingUnlinkedIc(false);
    }
  }, []);

  const linkOneIc = async (ic_id, label) => {
    if (linkingIcId) return;
    const socio_id = linkChoiceIc[ic_id] || null;
    if (!socio_id) { toast.error("Selecione um sócio."); return; }
    if (!window.confirm(`Vincular o parcelado/acordo de "${label}" ao sócio selecionado?`)) return;
    setLinkingIcId(ic_id);
    try {
      await api.post(`/installment-contracts/${ic_id}/link-socio`, { socio_id });
      toast.success("Sócio vinculado ao parcelado");
      await loadUnlinkedIc();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Falha ao vincular sócio");
    } finally {
      setLinkingIcId(null);
    }
  };

  const loadSocioOptions = useCallback(async () => {
    try {
      const { data } = await api.get("/socios");
      setSocioOptions((data || []).filter((s) => s.status === "ativo"));
      setSocioOptionsAll(data || []);
    } catch (_) {
      setSocioOptions([]);
      setSocioOptionsAll([]);
    }
  }, []);

  const loadRawDiag = useCallback(async () => {
    setLoadingRawDiag(true);
    try {
      const params = new URLSearchParams();
      if (rawTipo) params.set("tipo", rawTipo);
      if (rawSocioName.trim()) params.set("search_socio_name", rawSocioName.trim());
      if (rawClientName.trim()) params.set("search_client_name", rawClientName.trim());
      if (rawOnlyExcluded) params.set("only_excluded", "true");
      if (rawOnlyUnlinked) params.set("only_unlinked", "true");
      params.set("limit", "2000");
      const { data } = await api.get(`/audit/raw-exposure-diagnostic?${params.toString()}`);
      setRawDiag(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Falha ao carregar diagnóstico bruto");
    } finally {
      setLoadingRawDiag(false);
    }
  }, [rawTipo, rawSocioName, rawClientName, rawOnlyExcluded, rawOnlyUnlinked]);

  const exportRawDiagCsv = () => {
    if (!rawDiag?.items?.length) { toast.error("Nada para exportar"); return; }
    const cols = [
      "tipo_legivel","cliente_nome","record_id","client_id","socio_id_registro",
      "socio_id_cliente","socio_nome","status","capital_source","capital",
      "saldo_em_aberto","total_pago","capital_exposto","counts_in_exposure","reason","created_at",
    ];
    const esc = (v) => {
      if (v === null || v === undefined) return "";
      const s = String(v);
      if (s.includes(";") || s.includes('"') || s.includes("\n")) return `"${s.replaceAll('"','""')}"`;
      return s;
    };
    const lines = [cols.join(";")];
    for (const it of rawDiag.items) lines.push(cols.map((c) => esc(it[c])).join(";"));
    const blob = new Blob(["\ufeff" + lines.join("\n")], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `diagnostico-exposicao-${new Date().toISOString().slice(0,10)}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const loadBreakdown = useCallback(async (socio_id) => {
    if (!socio_id) { setBreakdown(null); return; }
    setLoadingBreakdown(true);
    try {
      const { data } = await api.get(`/audit/socio-exposure-breakdown/${socio_id}`);
      setBreakdown(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Falha ao carregar diagnóstico");
      setBreakdown(null);
    } finally {
      setLoadingBreakdown(false);
    }
  }, []);

  const loadOrphans = useCallback(async () => {
    setLoadingOrphans(true);
    try {
      const { data } = await api.get("/audit/orphan-active-contracts");
      setOrphans(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Falha ao listar contratos presos");
    } finally {
      setLoadingOrphans(false);
    }
  }, []);

  const loadRegErrors = useCallback(async () => {
    setLoadingRegErrors(true);
    try {
      const { data } = await api.get("/audit/registration-errors?limit=50");
      setRegErrors(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Falha ao carregar erros de cadastro");
    } finally {
      setLoadingRegErrors(false);
    }
  }, []);

  // Auto-load das duas novas seções ao montar a tela
  useEffect(() => {
    loadOrphans();
    loadRegErrors();
    loadUnlinked();
    loadUnlinkedIc();
    loadSocioOptions();
  }, [loadOrphans, loadRegErrors, loadUnlinked, loadUnlinkedIc, loadSocioOptions]);

  const fixOne = async (contract_id, label) => {
    if (fixingId || fixingAll) return;
    if (!window.confirm(`Corrigir o contrato "${label}"?\n\nO sistema vai detectar automaticamente se é caso "preso" (apenas alinha o status) ou se precisa registrar pagamento exato do saldo restante. Pagamentos NUNCA são duplicados.`)) return;
    setFixingId(contract_id);
    try {
      const { data } = await api.post(`/contracts/${contract_id}/quittance`);
      const closed_reason = data?.closed_reason || "quitacao";
      if (closed_reason.includes("self_heal") || closed_reason.includes("alinhamento")) {
        toast.success(`Status alinhado — sem novo pagamento (${closed_reason})`);
      } else {
        toast.success("Contrato quitado");
      }
      await loadOrphans();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Falha ao corrigir contrato");
    } finally {
      setFixingId(null);
    }
  };

  const fixAll = async () => {
    if (!orphans?.items?.length) return;
    if (fixingId || fixingAll) return;
    const total = orphans.items.length;
    if (!window.confirm(`Corrigir TODOS os ${total} contratos presos?\n\nSerão processados um por um. Cada contrato é verificado individualmente — casos sem saldo recebem apenas alinhamento de status (sem novo pagamento) e casos com saldo recebem pagamento exato do restante.\n\nIdempotente: contratos já quitados são ignorados sem efeito colateral.`)) return;
    setFixingAll(true);
    let ok_count = 0;
    let self_heal = 0;
    let fail = 0;
    const items = [...orphans.items];
    for (const it of items) {
      try {
        const { data } = await api.post(`/contracts/${it.contract_id}/quittance`);
        const cr = data?.closed_reason || "";
        if (cr.includes("self_heal") || cr.includes("alinhamento")) self_heal += 1;
        ok_count += 1;
      } catch (err) {
        fail += 1;
        // continua o loop mesmo se um falhar
      }
    }
    toast.success(`Processados: ${ok_count} OK (${self_heal} alinhamentos) · ${fail} falhas`, { duration: 6000 });
    setFixingAll(false);
    await loadOrphans();
  };

  const linkOne = async (contract_id, label) => {
    if (linkingId || linkingAll) return;
    const socio_id = linkChoice[contract_id] || null;
    if (!socio_id) {
      toast.error("Selecione um sócio antes de vincular.");
      return;
    }
    const socio = socioOptions.find((s) => s.id === socio_id);
    if (!window.confirm(`Vincular o contrato de "${label}" ao sócio "${socio?.name || socio_id}"?\n\nApenas o vínculo será criado — nenhum cálculo financeiro será refeito. Contratos já vinculados não são afetados.`)) return;
    setLinkingId(contract_id);
    try {
      await api.post(`/contracts/${contract_id}/link-socio`, { socio_id });
      toast.success(`Sócio vinculado: ${socio?.name || socio_id}`);
      await loadUnlinked();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Falha ao vincular sócio");
    } finally {
      setLinkingId(null);
    }
  };

  const linkAll = async () => {
    if (!unlinked?.items?.length) return;
    if (linkingId || linkingAll) return;
    // Apenas vincula os que TÊM sugestão (suggested_socio_id presente)
    const eligible = unlinked.items.filter((it) => it.suggested_socio_id);
    if (eligible.length === 0) {
      toast.error("Nenhum contrato possui sugestão automática. Vincule manualmente um por um.");
      return;
    }
    if (!window.confirm(`Vincular ${eligible.length} contrato(s) com a sugestão automática?\n\nContratos sem sugestão NÃO serão alterados — precisarão de vínculo manual depois.\n\nNenhum cálculo financeiro será refeito.`)) return;
    setLinkingAll(true);
    let ok_count = 0, fail = 0;
    for (const it of eligible) {
      try {
        await api.post(`/contracts/${it.contract_id}/link-socio`, { socio_id: it.suggested_socio_id });
        ok_count += 1;
      } catch (_) {
        fail += 1;
      }
    }
    toast.success(`Vinculados: ${ok_count} OK · ${fail} falhas · ${unlinked.items.length - eligible.length} sem sugestão (manual)`, { duration: 6000 });
    setLinkingAll(false);
    await loadUnlinked();
  };

  // Backfill em massa — corrige inconsistência de dados herdada
  const [backfilling, setBackfilling] = useState(false);
  const runBackfill = async () => {
    if (backfilling) return;
    if (!window.confirm(
      "Executar BACKFILL automático de vínculos de sócio?\n\n" +
      "Vai percorrer TODOS os contratos e parcelados sem `socio_id` e tentar " +
      "preencher automaticamente via `cliente.socio_id`. Registros sem cliente " +
      "vinculado a sócio ficam para correção manual.\n\n" +
      "NÃO altera regras financeiras nem registros já vinculados."
    )) return;
    setBackfilling(true);
    try {
      const { data } = await api.post("/audit/backfill-socio-links");
      const c = data.contracts, ic = data.installment_contracts;
      toast.success(
        `Backfill concluído: ${c.fixed_count} contratos + ${ic.fixed_count} parcelados vinculados. ` +
        `${c.unresolved_count + ic.unresolved_count} ficaram pendentes (sem cliente.socio_id).`,
        { duration: 8000 },
      );
      await Promise.all([loadUnlinked(), loadUnlinkedIc()]);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Falha no backfill");
    } finally {
      setBackfilling(false);
    }
  };

  const run = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/audit/full");
      setReport(data);
      toast.success("Auditoria concluída");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Falha ao executar auditoria");
    } finally {
      setLoading(false);
    }
  };

  const runCpf = async () => {
    setLoadingCpf(true);
    try {
      const { data } = await api.get("/audit/cpf-duplicates");
      setCpfReport(data);
      toast.success(`${data.total_duplicated_cpfs} CPF(s) duplicado(s) encontrado(s)`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Falha ao buscar duplicatas");
    } finally {
      setLoadingCpf(false);
    }
  };

  const ok = report?.summary?.ok;

  return (
    <Layout>
      <div className="mb-6">
        <div className="text-[11px] uppercase tracking-[0.2em] text-[#D4AF37]">Painel de manutenção</div>
        <h1 className="font-display text-3xl sm:text-[34px] font-bold tracking-tight mt-1" data-testid="audit-title">Auditoria Financeira</h1>
        <p className="text-[#A3A3A3] text-[13px] mt-1.5">
          Contratos presos, erros de cadastro público, divergências financeiras e logs de auditoria —
          tudo num só painel. Correções automáticas com self-heal idempotente.
        </p>
      </div>

      {/* ============================================================ */}
      {/* BACKFILL AUTOMÁTICO — correção em massa de socio_id legado    */}
      {/* ============================================================ */}
      <section className="fc-card p-4 sm:p-5 mb-6 border-cyan-500/40 bg-gradient-to-br from-cyan-500/[0.06] to-transparent" data-testid="backfill-section">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h2 className="font-display text-[15px] font-semibold tracking-tight flex items-center gap-2">
              <Wrench size={15} className="text-cyan-300"/> Backfill automático — vínculo de sócio (dados legados)
            </h2>
            <p className="text-[12px] text-[#A3A3A3] mt-1 max-w-2xl">
              Corrige inconsistência herdada: registros que aparecem com nome do sócio na UI mas
              têm <code className="text-[11px] text-cyan-300">socio_id</code> vazio no banco.
              Percorre TODOS os contratos e parcelados sem vínculo e preenche via <code className="text-[11px] text-cyan-300">cliente.socio_id</code>.
              <span className="text-emerald-300/80"> Não altera cálculos nem registros já vinculados.</span>
            </p>
          </div>
          <button
            onClick={runBackfill}
            disabled={backfilling}
            className="text-xs flex items-center gap-1.5 px-4 py-2 rounded-md border border-cyan-500/50 text-cyan-200 bg-cyan-500/10 hover:bg-cyan-500/20 transition-colors disabled:opacity-60 disabled:cursor-not-allowed whitespace-nowrap font-medium"
            data-testid="btn-backfill"
          >
            {backfilling ? <Loader2 size={13} className="animate-spin"/> : <Wrench size={13}/>}
            Executar Backfill agora
          </button>
        </div>
      </section>

      {/* ============================================================ */}
      {/* SEÇÃO 1 — CONTRATOS PRESOS (com Corrigir e Corrigir Todos)   */}
      {/* ============================================================ */}
      <section className="fc-card p-5 sm:p-6 mb-6 border-amber-500/30" data-testid="orphans-section">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-4">
          <div>
            <h2 className="font-display text-[16px] font-semibold tracking-tight flex items-center gap-2">
              <Wrench size={16} className="text-amber-300"/> Contratos presos
              {orphans && (
                <span className={`badge-base text-[11px] border ${
                  orphans.total === 0
                    ? "border-emerald-500/30 text-emerald-300 bg-emerald-500/5"
                    : "border-amber-500/40 text-amber-200 bg-amber-500/10"
                }`} data-testid="orphans-count">
                  {orphans.total === 0 ? "0 · tudo certo" : `${orphans.total} pendente(s)`}
                </span>
              )}
            </h2>
            <p className="text-[12px] text-[#A3A3A3] mt-1 max-w-2xl">
              Contratos com status &quot;ativo&quot; cujo ciclo atual já foi pago integralmente
              (saldo = 0) ou cujo ciclo está &quot;quitado&quot; mas o contrato continua ativo.
              O botão &quot;Corrigir&quot; executa a rotina de self-heal — nunca duplica pagamentos.
            </p>
          </div>
          <div className="flex flex-wrap gap-2 items-center">
            <button
              onClick={loadOrphans}
              disabled={loadingOrphans || fixingAll}
              className="fc-btn-ghost text-xs flex items-center gap-1.5"
              data-testid="btn-reload-orphans"
            >
              {loadingOrphans ? <Loader2 size={12} className="animate-spin"/> : <RefreshCcw size={12}/>}
              Atualizar
            </button>
            {orphans?.items?.length > 0 && (
              <button
                onClick={fixAll}
                disabled={fixingAll || fixingId || loadingOrphans}
                className="text-xs flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-emerald-500/40 text-emerald-300 bg-emerald-500/10 hover:bg-emerald-500/20 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                data-testid="btn-fix-all-orphans"
              >
                {fixingAll ? <Loader2 size={12} className="animate-spin"/> : <Wrench size={12}/>}
                Corrigir Todos ({orphans.items.length})
              </button>
            )}
          </div>
        </div>

        {loadingOrphans && !orphans ? (
          <div className="text-[13px] text-[#737373] flex items-center gap-2">
            <Loader2 size={14} className="animate-spin"/> Carregando…
          </div>
        ) : !orphans ? null : orphans.items.length === 0 ? (
          <div className="text-[13px] text-emerald-300/90 flex items-center gap-2">
            <CheckCircle2 size={14}/> Nenhum contrato preso encontrado
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-[12.5px]" data-testid="orphans-table">
              <thead>
                <tr className="text-[10.5px] uppercase tracking-wider text-[#737373] border-b border-[#1f1f1f]">
                  <th className="text-left py-2 px-2">Cliente</th>
                  <th className="text-left py-2 px-2">Sócio</th>
                  <th className="text-right py-2 px-2">Capital</th>
                  <th className="text-right py-2 px-2">Pago</th>
                  <th className="text-right py-2 px-2">Saldo</th>
                  <th className="text-left py-2 px-2">Motivo</th>
                  <th className="text-center py-2 px-2">Ação</th>
                </tr>
              </thead>
              <tbody>
                {orphans.items.map((c) => {
                  const isPreso = c.balance <= 0.005;
                  const motivoLabel = c.reason === "saldo_zerado_contrato_ativo"
                    ? "Saldo zerado · contrato ativo"
                    : c.reason === "ciclo_fechado_contrato_ativo"
                    ? "Ciclo fechado · contrato ativo"
                    : c.reason;
                  return (
                    <tr key={c.contract_id} className="border-b border-[#141414] hover:bg-amber-500/5" data-testid={`orphan-row-${c.contract_id}`}>
                      <td className="py-2 px-2">
                        <div className="text-white">{c.client_name || "—"}</div>
                        <div className="text-[10.5px] text-[#737373] font-mono-tab">#{c.contract_id.slice(0, 8)}</div>
                      </td>
                      <td className="py-2 px-2 text-[#A3A3A3]">{c.socio_name || "—"}</td>
                      <td className="py-2 px-2 text-right font-mono-tab">{formatBRL(c.capital)}</td>
                      <td className="py-2 px-2 text-right font-mono-tab text-emerald-300">{formatBRL(c.total_paid)}</td>
                      <td className={`py-2 px-2 text-right font-mono-tab ${isPreso ? "text-emerald-300" : "text-amber-300"}`}>
                        {formatBRL(c.balance)}
                      </td>
                      <td className="py-2 px-2 text-[11.5px] text-amber-200">{motivoLabel}</td>
                      <td className="py-2 px-2 text-center">
                        <button
                          onClick={() => fixOne(c.contract_id, c.client_name || c.contract_id.slice(0, 8))}
                          disabled={fixingId === c.contract_id || fixingAll}
                          className="text-[11.5px] inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md border border-emerald-500/40 text-emerald-300 bg-emerald-500/10 hover:bg-emerald-500/20 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                          data-testid={`btn-fix-${c.contract_id}`}
                        >
                          {fixingId === c.contract_id
                            ? <><Loader2 size={11} className="animate-spin"/> Corrigindo…</>
                            : <><Wrench size={11}/> Corrigir</>}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* ============================================================ */}
      {/* SEÇÃO 1.B — CONTRATOS SEM SÓCIO VINCULADO (dado legado)      */}
      {/* ============================================================ */}
      <section className="fc-card p-5 sm:p-6 mb-6 border-violet-500/30" data-testid="unlinked-section">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-4">
          <div>
            <h2 className="font-display text-[16px] font-semibold tracking-tight flex items-center gap-2">
              <Briefcase size={16} className="text-violet-300"/> Contratos sem sócio vinculado
              {unlinked && (
                <span className={`badge-base text-[11px] border ${
                  unlinked.total === 0
                    ? "border-emerald-500/30 text-emerald-300 bg-emerald-500/5"
                    : "border-violet-500/40 text-violet-200 bg-violet-500/10"
                }`} data-testid="unlinked-count">
                  {unlinked.total === 0 ? "0 · todos vinculados" : `${unlinked.total} pendente(s)`}
                </span>
              )}
            </h2>
            <p className="text-[12px] text-[#A3A3A3] mt-1 max-w-2xl">
              Contratos antigos (legado) sem <code className="text-[11px] text-violet-300">socio_id</code> e que NÃO são de Capital Próprio.
              Para cada um, o sistema sugere automaticamente um sócio via:
              {" "}<code className="text-[11px] text-violet-300">cliente.socio_id</code> →
              {" "}<code className="text-[11px] text-violet-300">contract_history</code>.
              Sem sugestão? Escolha manualmente abaixo.
              <span className="text-emerald-300/80"> Apenas vínculo é criado — nenhum cálculo financeiro é refeito.</span>
            </p>
          </div>
          <div className="flex flex-wrap gap-2 items-center">
            <button
              onClick={loadUnlinked}
              disabled={loadingUnlinked || linkingAll}
              className="fc-btn-ghost text-xs flex items-center gap-1.5"
              data-testid="btn-reload-unlinked"
            >
              {loadingUnlinked ? <Loader2 size={12} className="animate-spin"/> : <RefreshCcw size={12}/>}
              Atualizar
            </button>
            {unlinked?.items?.some((it) => it.suggested_socio_id) && (
              <button
                onClick={linkAll}
                disabled={linkingAll || linkingId || loadingUnlinked}
                className="text-xs flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-violet-500/40 text-violet-200 bg-violet-500/10 hover:bg-violet-500/20 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                data-testid="btn-link-all-unlinked"
              >
                {linkingAll ? <Loader2 size={12} className="animate-spin"/> : <Briefcase size={12}/>}
                Vincular todos com sugestão ({unlinked.items.filter(it => it.suggested_socio_id).length})
              </button>
            )}
          </div>
        </div>

        {loadingUnlinked && !unlinked ? (
          <div className="text-[13px] text-[#737373] flex items-center gap-2">
            <Loader2 size={14} className="animate-spin"/> Carregando…
          </div>
        ) : !unlinked ? null : unlinked.items.length === 0 ? (
          <div className="text-[13px] text-emerald-300/90 flex items-center gap-2">
            <CheckCircle2 size={14}/> Nenhum contrato sem sócio vinculado
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-[12.5px]" data-testid="unlinked-table">
              <thead>
                <tr className="text-[10.5px] uppercase tracking-wider text-[#737373] border-b border-[#1f1f1f]">
                  <th className="text-left py-2 px-2">Cliente</th>
                  <th className="text-right py-2 px-2">Capital</th>
                  <th className="text-left py-2 px-2">Status</th>
                  <th className="text-left py-2 px-2">Sugestão</th>
                  <th className="text-left py-2 px-2">Sócio escolhido</th>
                  <th className="text-center py-2 px-2">Ação</th>
                </tr>
              </thead>
              <tbody>
                {unlinked.items.map((c) => (
                  <tr key={c.contract_id} className="border-b border-[#141414] hover:bg-violet-500/5" data-testid={`unlinked-row-${c.contract_id}`}>
                    <td className="py-2 px-2">
                      <div className="text-white">{c.client_name || "—"}</div>
                      <div className="text-[10.5px] text-[#737373] font-mono-tab">#{c.contract_id.slice(0, 8)}</div>
                    </td>
                    <td className="py-2 px-2 text-right font-mono-tab">{formatBRL(c.capital)}</td>
                    <td className="py-2 px-2 capitalize text-[#A3A3A3]">{c.status}</td>
                    <td className="py-2 px-2 text-[11.5px]">
                      {c.suggested_socio_name ? (
                        <>
                          <span className="text-violet-200 font-medium">{c.suggested_socio_name}</span>
                          <div className="text-[10px] text-[#737373] mt-0.5">via {c.suggestion_source}</div>
                          {c.suggested_socio_active === false && (
                            <div className="text-[10px] text-amber-300 mt-0.5">⚠ sócio inativo</div>
                          )}
                        </>
                      ) : (
                        <span className="text-[#737373] italic">sem sugestão</span>
                      )}
                    </td>
                    <td className="py-2 px-2">
                      <select
                        className="fc-input text-[12px] py-1 px-2 min-w-[180px]"
                        value={linkChoice[c.contract_id] || ""}
                        onChange={(e) => setLinkChoice((p) => ({ ...p, [c.contract_id]: e.target.value }))}
                        disabled={linkingId === c.contract_id || linkingAll}
                        data-testid={`select-socio-${c.contract_id}`}
                      >
                        <option value="">— selecionar —</option>
                        {socioOptions.map((s) => (
                          <option key={s.id} value={s.id}>{s.name}</option>
                        ))}
                      </select>
                    </td>
                    <td className="py-2 px-2 text-center">
                      <button
                        onClick={() => linkOne(c.contract_id, c.client_name || c.contract_id.slice(0, 8))}
                        disabled={linkingId === c.contract_id || linkingAll || !linkChoice[c.contract_id]}
                        className="text-[11.5px] inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md border border-violet-500/40 text-violet-200 bg-violet-500/10 hover:bg-violet-500/20 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                        data-testid={`btn-link-${c.contract_id}`}
                      >
                        {linkingId === c.contract_id
                          ? <><Loader2 size={11} className="animate-spin"/> Vinculando…</>
                          : <><Briefcase size={11}/> Vincular</>}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* ============================================================ */}
      {/* SEÇÃO 1.D — PARCELADOS / ACORDOS SEM SÓCIO VINCULADO         */}
      {/* ============================================================ */}
      <section className="fc-card p-5 sm:p-6 mb-6 border-fuchsia-500/30" data-testid="unlinked-ic-section">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-4">
          <div>
            <h2 className="font-display text-[16px] font-semibold tracking-tight flex items-center gap-2">
              <Briefcase size={16} className="text-fuchsia-300"/> Parcelados / Acordos sem sócio vinculado
              {unlinkedIc && (
                <span className={`badge-base text-[11px] border ${
                  unlinkedIc.total === 0
                    ? "border-emerald-500/30 text-emerald-300 bg-emerald-500/5"
                    : "border-fuchsia-500/40 text-fuchsia-200 bg-fuchsia-500/10"
                }`}>
                  {unlinkedIc.total === 0 ? "0 · todos vinculados" : `${unlinkedIc.total} pendente(s)`}
                </span>
              )}
            </h2>
            <p className="text-[12px] text-[#A3A3A3] mt-1 max-w-2xl">
              Parcelados e acordos legados sem <code className="text-[11px] text-fuchsia-300">socio_id</code>.
              Por isso não aparecem na exposição do sócio até serem vinculados. Mesma lógica defensiva da seção de contratos.
            </p>
          </div>
          <button
            onClick={loadUnlinkedIc}
            disabled={loadingUnlinkedIc}
            className="fc-btn-ghost text-xs flex items-center gap-1.5"
          >
            {loadingUnlinkedIc ? <Loader2 size={12} className="animate-spin"/> : <RefreshCcw size={12}/>}
            Atualizar
          </button>
        </div>

        {loadingUnlinkedIc && !unlinkedIc ? (
          <div className="text-[13px] text-[#737373] flex items-center gap-2"><Loader2 size={14} className="animate-spin"/> Carregando…</div>
        ) : !unlinkedIc ? null : unlinkedIc.items.length === 0 ? (
          <div className="text-[13px] text-emerald-300/90 flex items-center gap-2"><CheckCircle2 size={14}/> Nenhum parcelado/acordo sem sócio</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-[12.5px]">
              <thead>
                <tr className="text-[10.5px] uppercase tracking-wider text-[#737373] border-b border-[#1f1f1f]">
                  <th className="text-left py-2 px-2">Cliente</th>
                  <th className="text-left py-2 px-2">Tipo</th>
                  <th className="text-left py-2 px-2">Status</th>
                  <th className="text-right py-2 px-2">Capital</th>
                  <th className="text-right py-2 px-2">Saldo</th>
                  <th className="text-left py-2 px-2">Sugestão</th>
                  <th className="text-left py-2 px-2">Sócio escolhido</th>
                  <th className="text-center py-2 px-2">Ação</th>
                </tr>
              </thead>
              <tbody>
                {unlinkedIc.items.map((c) => (
                  <tr key={c.installment_contract_id} className="border-b border-[#141414] hover:bg-fuchsia-500/5">
                    <td className="py-2 px-2">
                      <div className="text-white">{c.client_name || "—"}</div>
                      <div className="text-[10.5px] text-[#737373] font-mono-tab">#{c.installment_contract_id.slice(0, 8)}</div>
                    </td>
                    <td className="py-2 px-2 capitalize text-[11.5px] text-fuchsia-200">{c.kind}</td>
                    <td className="py-2 px-2 capitalize text-[#A3A3A3]">{c.status}</td>
                    <td className="py-2 px-2 text-right font-mono-tab">{formatBRL(c.capital_nominal)}</td>
                    <td className="py-2 px-2 text-right font-mono-tab text-amber-300">{formatBRL(c.balance_remaining)}</td>
                    <td className="py-2 px-2 text-[11.5px]">
                      {c.suggested_socio_name ? (
                        <>
                          <span className="text-fuchsia-200">{c.suggested_socio_name}</span>
                          <div className="text-[10px] text-[#737373]">via {c.suggestion_source}</div>
                        </>
                      ) : <span className="text-[#737373] italic">sem sugestão</span>}
                    </td>
                    <td className="py-2 px-2">
                      <select
                        className="fc-input text-[12px] py-1 px-2 min-w-[180px]"
                        value={linkChoiceIc[c.installment_contract_id] || ""}
                        onChange={(e) => setLinkChoiceIc((p) => ({ ...p, [c.installment_contract_id]: e.target.value }))}
                        disabled={linkingIcId === c.installment_contract_id}
                      >
                        <option value="">— selecionar —</option>
                        {socioOptions.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                      </select>
                    </td>
                    <td className="py-2 px-2 text-center">
                      <button
                        onClick={() => linkOneIc(c.installment_contract_id, c.client_name || c.installment_contract_id.slice(0, 8))}
                        disabled={linkingIcId === c.installment_contract_id || !linkChoiceIc[c.installment_contract_id]}
                        className="text-[11.5px] inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md border border-fuchsia-500/40 text-fuchsia-200 bg-fuchsia-500/10 hover:bg-fuchsia-500/20 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                      >
                        {linkingIcId === c.installment_contract_id
                          ? <><Loader2 size={11} className="animate-spin"/> Vinculando…</>
                          : <><Briefcase size={11}/> Vincular</>}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* ============================================================ */}
      {/* SEÇÃO 1.C — DIAGNÓSTICO DE EXPOSIÇÃO POR SÓCIO              */}
      {/* ============================================================ */}
      <section className="fc-card p-5 sm:p-6 mb-6 border-cyan-500/30" data-testid="exposure-breakdown-section">
        <div className="mb-4">
          <h2 className="font-display text-[16px] font-semibold tracking-tight flex items-center gap-2">
            <TrendingUp size={16} className="text-cyan-300"/> Conferência detalhada da exposição por sócio
          </h2>
          <p className="text-[12px] text-[#A3A3A3] mt-1 max-w-2xl">
            Selecione um sócio para ver linha-por-linha quais contratos e parcelados estão somando
            no <code className="text-[11px] text-cyan-300">capital_na_rua</code>. Útil para investigar
            divergências sem mexer no banco.
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-3 mb-4">
          <select
            className="fc-input text-[13px] min-w-[300px]"
            value={selectedSocioId}
            onChange={(e) => { setSelectedSocioId(e.target.value); loadBreakdown(e.target.value); }}
            data-testid="select-socio-breakdown"
          >
            <option value="">— selecionar sócio —</option>
            {socioOptionsAll.map((s) => (
              <option key={s.id} value={s.id}>{s.name} ({s.status})</option>
            ))}
          </select>
          {selectedSocioId && (
            <button
              onClick={() => loadBreakdown(selectedSocioId)}
              disabled={loadingBreakdown}
              className="fc-btn-ghost text-xs flex items-center gap-1.5"
              data-testid="btn-reload-breakdown"
            >
              {loadingBreakdown ? <Loader2 size={12} className="animate-spin"/> : <RefreshCcw size={12}/>}
              Atualizar
            </button>
          )}
        </div>

        {loadingBreakdown ? (
          <div className="text-[13px] text-[#737373] flex items-center gap-2">
            <Loader2 size={14} className="animate-spin"/> Carregando…
          </div>
        ) : !breakdown ? (
          <div className="text-[12px] text-[#737373] italic">Selecione um sócio acima para ver o detalhamento.</div>
        ) : (
          <>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
              <div className="rounded-md border border-[#1f1f1f] bg-[#0a0a0a] px-3 py-2">
                <div className="text-[10px] uppercase tracking-wider text-[#737373]">Exposição contratos</div>
                <div className="font-mono-tab text-base text-amber-300">{formatBRL(breakdown.total_exposicao_contratos)}</div>
              </div>
              <div className="rounded-md border border-[#1f1f1f] bg-[#0a0a0a] px-3 py-2">
                <div className="text-[10px] uppercase tracking-wider text-[#737373]">Exposição parcelados</div>
                <div className="font-mono-tab text-base text-amber-300">{formatBRL(breakdown.total_exposicao_parcelados)}</div>
              </div>
              <div className="rounded-md border border-cyan-500/30 bg-cyan-500/5 px-3 py-2">
                <div className="text-[10px] uppercase tracking-wider text-cyan-300">Exposição total</div>
                <div className="font-mono-tab text-base text-cyan-200">{formatBRL(breakdown.total_exposicao)}</div>
              </div>
              <div className="rounded-md border border-[#1f1f1f] bg-[#0a0a0a] px-3 py-2">
                <div className="text-[10px] uppercase tracking-wider text-[#737373]">Ignorados (safety net)</div>
                <div className="font-mono-tab text-base text-[#737373]">{formatBRL(breakdown.total_ignorados_safety_net)}</div>
              </div>
            </div>

            {/* Composição linha-por-linha — relatório de auditoria solicitado */}
            <div className="rounded-md border border-cyan-500/20 bg-cyan-500/[0.03] px-4 py-3 mb-4 text-[12px]">
              <div className="text-[11px] uppercase tracking-wider text-cyan-300 mb-2">
                📊 Composição da exposição (resumo)
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1 text-[12.5px]">
                <div className="flex justify-between">
                  <span className="text-[#A3A3A3]">Contratos normais que somam:</span>
                  <span className="font-mono-tab text-white">
                    {breakdown.contratos_normais.filter(c => c.counted_in_exposure).length}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-[#A3A3A3]">Parcelados/Acordos que somam:</span>
                  <span className="font-mono-tab text-white">
                    {breakdown.parcelados_acordos.filter(p => p.counted_in_exposure).length}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-[#A3A3A3]">Contratos ignorados (safety net):</span>
                  <span className="font-mono-tab text-violet-300">
                    {breakdown.contratos_normais.filter(c => c.is_stuck).length}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-[#A3A3A3]">Quitados/cancelados (não contam):</span>
                  <span className="font-mono-tab text-emerald-300">
                    {breakdown.contratos_normais.filter(c => !c.counted_in_exposure && !c.is_stuck).length}
                  </span>
                </div>
              </div>
            </div>

            <h3 className="text-[12px] uppercase tracking-wider text-cyan-300 mb-2 mt-4">Contratos normais ({breakdown.contratos_normais.length})</h3>
            <div className="overflow-x-auto">
              <table className="w-full text-[12px]">
                <thead>
                  <tr className="text-[10px] uppercase tracking-wider text-[#737373] border-b border-[#1f1f1f]">
                    <th className="text-left py-1.5 px-2">Cliente</th>
                    <th className="text-center py-1.5 px-2">Status</th>
                    <th className="text-right py-1.5 px-2">Capital</th>
                    <th className="text-right py-1.5 px-2">Saldo ciclo</th>
                    <th className="text-center py-1.5 px-2">Ciclo</th>
                    <th className="text-right py-1.5 px-2">Exposto?</th>
                  </tr>
                </thead>
                <tbody>
                  {breakdown.contratos_normais.map((c) => (
                    <tr key={c.contract_id} className={`border-b border-[#141414] ${c.counted_in_exposure ? "bg-amber-500/5" : ""}`}>
                      <td className="py-1 px-2">
                        {c.client_name || "—"}
                        <div className="text-[10px] text-[#737373] font-mono-tab">#{c.contract_id.slice(0, 8)}</div>
                      </td>
                      <td className="py-1 px-2 text-center capitalize text-[#A3A3A3]">{c.status}</td>
                      <td className="py-1 px-2 text-right font-mono-tab">{formatBRL(c.capital_nominal)}</td>
                      <td className="py-1 px-2 text-right font-mono-tab text-[#A3A3A3]">
                        {c.cycle_balance != null ? formatBRL(c.cycle_balance) : "—"}
                      </td>
                      <td className="py-1 px-2 text-center text-[10.5px] text-[#737373]">{c.cycle_status || "—"}</td>
                      <td className="py-1 px-2 text-right">
                        {c.counted_in_exposure ? (
                          <span className="text-amber-300 font-mono-tab font-medium">{formatBRL(c.capital_exposto)} ✓</span>
                        ) : c.is_stuck ? (
                          <span className="text-[10px] text-violet-300">🛡 ignorado ({c.stuck_reason})</span>
                        ) : (
                          <span className="text-[10px] text-[#737373]">não conta</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {breakdown.parcelados_acordos.length > 0 && (
              <>
                <h3 className="text-[12px] uppercase tracking-wider text-cyan-300 mb-2 mt-6">Parcelados / Acordos ({breakdown.parcelados_acordos.length})</h3>
                <div className="overflow-x-auto">
                  <table className="w-full text-[12px]">
                    <thead>
                      <tr className="text-[10px] uppercase tracking-wider text-[#737373] border-b border-[#1f1f1f]">
                        <th className="text-left py-1.5 px-2">Cliente</th>
                        <th className="text-left py-1.5 px-2">Tipo</th>
                        <th className="text-center py-1.5 px-2">Status</th>
                        <th className="text-right py-1.5 px-2">Capital original</th>
                        <th className="text-right py-1.5 px-2">Total pago</th>
                        <th className="text-right py-1.5 px-2">Saldo em aberto</th>
                        <th className="text-center py-1.5 px-2">Parcelas abertas</th>
                        <th className="text-right py-1.5 px-2">Entra em exposição?</th>
                      </tr>
                    </thead>
                    <tbody>
                      {breakdown.parcelados_acordos.map((p) => (
                        <tr key={p.installment_contract_id} className={`border-b border-[#141414] ${p.capital_exposto > 0 ? "bg-amber-500/5" : ""}`}>
                          <td className="py-1 px-2">
                            {p.client_name || "—"}
                            <div className="text-[10px] text-[#737373] font-mono-tab">#{p.installment_contract_id.slice(0, 8)}</div>
                          </td>
                          <td className="py-1 px-2 capitalize text-[11px] text-cyan-300">{p.kind || "—"}</td>
                          <td className="py-1 px-2 text-center capitalize text-[#A3A3A3]">{p.status}</td>
                          <td className="py-1 px-2 text-right font-mono-tab">{formatBRL(p.capital_nominal)}</td>
                          <td className="py-1 px-2 text-right font-mono-tab text-emerald-300">{formatBRL(p.total_paid)}</td>
                          <td className="py-1 px-2 text-right font-mono-tab text-amber-300">{formatBRL(p.balance_remaining)}</td>
                          <td className="py-1 px-2 text-center text-[#A3A3A3]">{p.open_installments}</td>
                          <td className="py-1 px-2 text-right">
                            {p.counted_in_exposure ? (
                              <span className="text-amber-300 font-mono-tab font-medium">{formatBRL(p.capital_exposto)} ✓</span>
                            ) : (
                              <span className="text-[10px] text-[#737373]">não conta</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </>
        )}
      </section>

      {/* ============================================================ */}
      {/* SEÇÃO 1.D — DIAGNÓSTICO BRUTO UNIFICADO (CONTRATOS+PARC+ACR) */}
      {/* ============================================================ */}
      <section className="fc-card p-5 sm:p-6 mb-6 border-fuchsia-500/30" data-testid="raw-diagnostic-section">
        <div className="mb-4">
          <h2 className="font-display text-[16px] font-semibold tracking-tight flex items-center gap-2">
            <ShieldAlert size={16} className="text-fuchsia-300"/> Diagnóstico bruto — Exposição completa
          </h2>
          <p className="text-[12px] text-[#A3A3A3] mt-1 max-w-3xl">
            Visão definitiva (read-only) com TODOS os contratos, parcelados e acordos —
            linha-a-linha, com motivo exato quando algum registro NÃO entra na exposição.
            Use para descobrir qualquer divergência futura sem precisar alterar código.
            Compara automaticamente o calculado vs o esperado pelo dashboard.
          </p>
        </div>

        {/* Filtros */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-2 mb-4">
          <select
            className="fc-input text-[12.5px] lg:col-span-1"
            value={rawTipo}
            onChange={(e) => setRawTipo(e.target.value)}
            data-testid="raw-diag-tipo"
          >
            <option value="">Tipo: todos</option>
            <option value="contrato">Contrato normal</option>
            <option value="parcelado">Parcelado</option>
            <option value="acordo">Acordo</option>
          </select>
          <input
            type="text"
            placeholder="Buscar por nome do sócio"
            className="fc-input text-[12.5px] lg:col-span-2"
            value={rawSocioName}
            onChange={(e) => setRawSocioName(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") loadRawDiag(); }}
            data-testid="raw-diag-search-socio"
          />
          <input
            type="text"
            placeholder="Buscar por nome do cliente"
            className="fc-input text-[12.5px] lg:col-span-2"
            value={rawClientName}
            onChange={(e) => setRawClientName(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") loadRawDiag(); }}
            data-testid="raw-diag-search-client"
          />
          <div className="flex items-center gap-1 lg:col-span-1">
            <button
              onClick={loadRawDiag}
              disabled={loadingRawDiag}
              className="flex-1 text-xs flex items-center justify-center gap-1.5 px-3 py-2 rounded-md border border-fuchsia-500/50 text-fuchsia-200 bg-fuchsia-500/10 hover:bg-fuchsia-500/20 transition-colors disabled:opacity-60"
              data-testid="raw-diag-search-btn"
            >
              {loadingRawDiag ? <Loader2 size={12} className="animate-spin"/> : <ShieldCheck size={12}/>}
              Buscar
            </button>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-4 mb-4 text-[12px] text-[#A3A3A3]">
          <label className="inline-flex items-center gap-1.5 cursor-pointer">
            <input
              type="checkbox"
              checked={rawOnlyExcluded}
              onChange={(e) => setRawOnlyExcluded(e.target.checked)}
              data-testid="raw-diag-only-excluded"
            />
            Apenas FORA da exposição
          </label>
          <label className="inline-flex items-center gap-1.5 cursor-pointer">
            <input
              type="checkbox"
              checked={rawOnlyUnlinked}
              onChange={(e) => setRawOnlyUnlinked(e.target.checked)}
              data-testid="raw-diag-only-unlinked"
            />
            Apenas sem socio_id no registro
          </label>
          {rawDiag && (
            <button
              onClick={exportRawDiagCsv}
              className="text-xs ml-auto flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-[#1f1f1f] text-[#D4AF37] hover:bg-[#0f0f0f] transition-colors"
              data-testid="raw-diag-export-csv"
            >
              <FileText size={12}/> Exportar CSV ({rawDiag.items.length})
            </button>
          )}
        </div>

        {/* Resumo */}
        {rawDiag && (
          <>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-3">
              <div className="rounded-md border border-[#1f1f1f] bg-[#0a0a0a] px-3 py-2">
                <div className="text-[10px] uppercase tracking-wider text-[#737373]">Capital em contratos normais</div>
                <div className="font-mono-tab text-base text-amber-300">{formatBRL(rawDiag.summary.calc_capital_contratos_normais)}</div>
                <div className="text-[10px] text-[#737373] mt-0.5">{rawDiag.summary.totals_count.contratos} registros</div>
              </div>
              <div className="rounded-md border border-[#1f1f1f] bg-[#0a0a0a] px-3 py-2">
                <div className="text-[10px] uppercase tracking-wider text-[#737373]">Capital em parcelados</div>
                <div className="font-mono-tab text-base text-amber-300">{formatBRL(rawDiag.summary.calc_capital_parcelados)}</div>
                <div className="text-[10px] text-[#737373] mt-0.5">{rawDiag.summary.totals_count.parcelados} registros</div>
              </div>
              <div className="rounded-md border border-[#1f1f1f] bg-[#0a0a0a] px-3 py-2">
                <div className="text-[10px] uppercase tracking-wider text-[#737373]">Capital em acordos</div>
                <div className="font-mono-tab text-base text-amber-300">{formatBRL(rawDiag.summary.calc_capital_acordos)}</div>
                <div className="text-[10px] text-[#737373] mt-0.5">{rawDiag.summary.totals_count.acordos} registros</div>
              </div>
              <div className="rounded-md border border-cyan-500/30 bg-cyan-500/5 px-3 py-2">
                <div className="text-[10px] uppercase tracking-wider text-cyan-300">Total da exposição</div>
                <div className="font-mono-tab text-base text-cyan-200">{formatBRL(rawDiag.summary.calc_total_exposicao)}</div>
                <div className="text-[10px] text-[#737373] mt-0.5">{rawDiag.summary.totals_count.in_exposure} contam · {rawDiag.summary.totals_count.excluded} fora</div>
              </div>
            </div>
            {/* Comparação vs Dashboard */}
            {rawDiag.summary.expected ? (
              <div className="rounded-md border border-[#1f1f1f] bg-[#0a0a0a] px-3 py-3 mb-3" data-testid="raw-diag-compare">
                <div className="text-[11px] uppercase tracking-wider text-[#737373] mb-2">Comparação com Dashboard (esperado)</div>
                {rawDiag.summary.expected.error ? (
                  <div className="text-[12px] text-red-300">Falha ao carregar dashboard: {rawDiag.summary.expected.error}</div>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-[12.5px]">
                    <div>
                      <span className="text-[#737373]">Esperado contratos:</span>{" "}
                      <span className="font-mono-tab text-amber-300">{formatBRL(rawDiag.summary.expected.capital_contratos)}</span>
                    </div>
                    <div>
                      <span className="text-[#737373]">Esperado parcelados+acordos:</span>{" "}
                      <span className="font-mono-tab text-amber-300">{formatBRL(rawDiag.summary.expected.capital_parcelados_acordos)}</span>
                    </div>
                    <div>
                      <span className="text-[#737373]">Esperado TOTAL:</span>{" "}
                      <span className="font-mono-tab text-cyan-200">{formatBRL(rawDiag.summary.expected.capital_total_exposto)}</span>
                    </div>
                    <div className="sm:col-span-3 mt-1 pt-2 border-t border-[#1f1f1f]">
                      <span className="text-[#737373]">Diferença calculado − esperado:</span>{" "}
                      <span className={`font-mono-tab font-medium ${Math.abs(rawDiag.summary.diff_total || 0) < 0.05 ? "text-emerald-300" : "text-red-300"}`} data-testid="raw-diag-diff">
                        {rawDiag.summary.diff_total > 0 ? "+" : ""}{formatBRL(rawDiag.summary.diff_total)}
                      </span>
                      <span className="text-[10.5px] text-[#737373] ml-2">
                        (capital safety-net: dashboard usa soma direta dos ativos; calculado aqui aplica regra de ciclo aberto + saldo &gt; 0)
                      </span>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="text-[11px] text-[#737373] italic mb-3" data-testid="raw-diag-filters-active">
                Comparação com dashboard desativada — filtros estão ativos.
              </div>
            )}
            {rawDiag.truncated && (
              <div className="text-[11px] text-amber-300 mb-2">
                Exibindo {rawDiag.items.length} de {rawDiag.items_total} registros. Use filtros ou exporte para CSV para ver tudo.
              </div>
            )}
          </>
        )}

        {/* Tabela */}
        {loadingRawDiag && !rawDiag ? (
          <div className="text-[13px] text-[#737373] flex items-center gap-2">
            <Loader2 size={14} className="animate-spin"/> Carregando…
          </div>
        ) : !rawDiag ? (
          <div className="text-[12px] text-[#737373] italic">Clique em &quot;Buscar&quot; para carregar todos os registros.</div>
        ) : rawDiag.items.length === 0 ? (
          <div className="text-[13px] text-[#737373] flex items-center gap-2">
            <CheckCircle2 size={14}/> Nenhum registro encontrado com esses filtros.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-[11.5px]" data-testid="raw-diag-table">
              <thead>
                <tr className="text-[10px] uppercase tracking-wider text-[#737373] border-b border-[#1f1f1f]">
                  <th className="text-left py-2 px-2">Tipo</th>
                  <th className="text-left py-2 px-2">Cliente</th>
                  <th className="text-left py-2 px-2">Sócio</th>
                  <th className="text-left py-2 px-2">Status</th>
                  <th className="text-right py-2 px-2">Capital</th>
                  <th className="text-right py-2 px-2">Saldo</th>
                  <th className="text-right py-2 px-2">Pago</th>
                  <th className="text-right py-2 px-2">Exposto</th>
                  <th className="text-center py-2 px-2">Entra?</th>
                  <th className="text-left py-2 px-2">Motivo</th>
                </tr>
              </thead>
              <tbody>
                {rawDiag.items.map((it) => (
                  <tr key={`${it.tipo}-${it.record_id}`} className={`border-b border-[#141414] ${it.counts_in_exposure ? "" : "bg-[#0a0a0a]/40"}`}>
                    <td className="py-1.5 px-2">
                      <span className={`badge-base text-[10px] border ${
                        it.tipo === "contrato"
                          ? "border-[#D4AF37]/30 text-[#D4AF37] bg-[#0f0f0f]"
                          : it.tipo === "parcelado"
                          ? "border-cyan-500/30 text-cyan-300 bg-[#0f0f0f]"
                          : "border-fuchsia-500/30 text-fuchsia-300 bg-[#0f0f0f]"
                      }`}>{it.tipo_legivel}</span>
                    </td>
                    <td className="py-1.5 px-2">
                      <div>{it.cliente_nome || <span className="text-[#737373] italic">sem nome</span>}</div>
                      <div className="text-[9.5px] text-[#737373] font-mono-tab">#{(it.record_id || "").slice(0, 8)}</div>
                    </td>
                    <td className="py-1.5 px-2">
                      <div className="text-[#A3A3A3]">{it.socio_nome || <span className="text-red-300 italic">sem sócio</span>}</div>
                      <div className="text-[9.5px] text-[#737373] font-mono-tab">
                        reg: {it.socio_id_registro ? it.socio_id_registro.slice(0,8) : "—"} · cli: {it.socio_id_cliente ? it.socio_id_cliente.slice(0,8) : "—"}
                      </div>
                    </td>
                    <td className="py-1.5 px-2 capitalize text-[#A3A3A3]">{it.status || "—"}</td>
                    <td className="py-1.5 px-2 text-right font-mono-tab">{formatBRL(it.capital)}</td>
                    <td className="py-1.5 px-2 text-right font-mono-tab text-amber-300">{formatBRL(it.saldo_em_aberto)}</td>
                    <td className="py-1.5 px-2 text-right font-mono-tab text-emerald-300">{formatBRL(it.total_pago)}</td>
                    <td className="py-1.5 px-2 text-right font-mono-tab font-medium">
                      {it.counts_in_exposure ? (
                        <span className="text-cyan-300">{formatBRL(it.capital_exposto)}</span>
                      ) : (
                        <span className="text-[#737373]">—</span>
                      )}
                    </td>
                    <td className="py-1.5 px-2 text-center">
                      {it.counts_in_exposure
                        ? <CheckCircle2 size={14} className="inline text-emerald-400"/>
                        : <XCircle size={14} className="inline text-red-400"/>}
                    </td>
                    <td className="py-1.5 px-2 text-[10.5px] text-[#A3A3A3]" data-testid={`raw-diag-reason-${it.record_id}`}>{it.reason}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>


      {/* ============================================================ */}
      {/* SEÇÃO 2 — ERROS DE CADASTRO PÚBLICO                          */}
      {/* ============================================================ */}
      <section className="fc-card p-5 sm:p-6 mb-6" data-testid="registration-errors-section">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-4">
          <div>
            <h2 className="font-display text-[16px] font-semibold tracking-tight flex items-center gap-2">
              <ScrollText size={16} className="text-[#D4AF37]"/> Erros de cadastro público
              {regErrors && (
                <span className="badge-base text-[11px] border border-[#D4AF37]/30 text-[#D4AF37] bg-[#0f0f0f]" data-testid="reg-errors-count">
                  {regErrors.total} total · {regErrors.items.length} exibidos
                </span>
              )}
            </h2>
            <p className="text-[12px] text-[#A3A3A3] mt-1 max-w-2xl">
              Falhas registradas no fluxo /cadastro público — útil para entender por que clientes
              não conseguem completar o envio. Etapas: <code className="text-[11px] text-[#D4AF37]">validation</code> ·
              <code className="text-[11px] text-[#D4AF37]"> duplicate</code> ·
              <code className="text-[11px] text-[#D4AF37]"> storage</code> ·
              <code className="text-[11px] text-[#D4AF37]"> upload</code> ·
              <code className="text-[11px] text-[#D4AF37]"> database</code>.
            </p>
          </div>
          <button
            onClick={loadRegErrors}
            disabled={loadingRegErrors}
            className="fc-btn-ghost text-xs flex items-center gap-1.5"
            data-testid="btn-reload-reg-errors"
          >
            {loadingRegErrors ? <Loader2 size={12} className="animate-spin"/> : <RefreshCcw size={12}/>}
            Atualizar
          </button>
        </div>

        {regErrors?.by_stage_last_7d?.length > 0 && (
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 mb-4">
            {regErrors.by_stage_last_7d.map((s) => (
              <div key={s.stage} className="rounded-md border border-[#1f1f1f] bg-[#0a0a0a] px-3 py-2">
                <div className="text-[10px] uppercase tracking-wider text-[#737373]">{s.stage}</div>
                <div className="font-mono-tab text-base mt-0.5">{s.count}</div>
                <div className="text-[10px] text-[#737373]">últimos 7 dias</div>
              </div>
            ))}
          </div>
        )}

        {loadingRegErrors && !regErrors ? (
          <div className="text-[13px] text-[#737373] flex items-center gap-2">
            <Loader2 size={14} className="animate-spin"/> Carregando…
          </div>
        ) : !regErrors ? null : regErrors.items.length === 0 ? (
          <div className="text-[13px] text-emerald-300/90 flex items-center gap-2">
            <CheckCircle2 size={14}/> Nenhum erro de cadastro registrado
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-[12px]" data-testid="reg-errors-table">
              <thead>
                <tr className="text-[10.5px] uppercase tracking-wider text-[#737373] border-b border-[#1f1f1f]">
                  <th className="text-left py-2 px-2">Quando</th>
                  <th className="text-left py-2 px-2">Etapa</th>
                  <th className="text-left py-2 px-2">CPF</th>
                  <th className="text-left py-2 px-2">WhatsApp</th>
                  <th className="text-left py-2 px-2">Indicador</th>
                  <th className="text-left py-2 px-2">Mensagem</th>
                  <th className="text-center py-2 px-2">Status</th>
                </tr>
              </thead>
              <tbody>
                {regErrors.items.map((e) => (
                  <tr key={e.id} className="border-b border-[#141414]">
                    <td className="py-1.5 px-2 text-[10.5px] text-[#737373]">{(e.created_at || "").slice(0, 19).replace("T", " ")}</td>
                    <td className="py-1.5 px-2">
                      <span className={`badge-base text-[10px] ${
                        e.stage === "validation" ? "border-amber-500/30 text-amber-200"
                        : e.stage === "duplicate" ? "border-red-500/30 text-red-200"
                        : e.stage === "storage" || e.stage === "upload" ? "border-blue-500/30 text-blue-200"
                        : "border-[#262626] text-[#A3A3A3]"
                      }`}>{e.stage}</span>
                    </td>
                    <td className="py-1.5 px-2 font-mono-tab text-[#A3A3A3]">{e.cpf || "—"}</td>
                    <td className="py-1.5 px-2 text-[#A3A3A3]">{e.whatsapp || "—"}</td>
                    <td className="py-1.5 px-2 text-[#A3A3A3]">{e.indicator_code || "—"}</td>
                    <td className="py-1.5 px-2 text-[#E0E0E0] max-w-[420px] truncate" title={e.error_message}>{e.error_message}</td>
                    <td className="py-1.5 px-2 text-center font-mono-tab text-[#737373]">{e.status_code || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* ============================================================ */}
      {/* SEÇÃO 3 — DIVERGÊNCIAS FINANCEIRAS (existente)               */}
      {/* ============================================================ */}

      {/* Top summary card */}
      <div className={`fc-card p-5 sm:p-6 mb-6 ${
        report ? (ok ? "border-emerald-500/30" : "border-red-500/30") : ""
      }`} data-testid="audit-summary-card">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex items-center gap-3">
            {!report
              ? <ShieldCheck size={32} className="text-[#737373]"/>
              : ok
              ? <ShieldCheck size={32} className="text-emerald-400"/>
              : <ShieldAlert size={32} className="text-red-400"/>}
            <div>
              <div className="text-[11px] uppercase tracking-wider text-[#737373]">Status</div>
              <div className="text-xl font-display font-semibold">
                {!report
                  ? "Aguardando execução"
                  : ok
                  ? <span className="text-emerald-300">🟢 Sistema íntegro</span>
                  : <span className="text-red-300">🔴 {report.summary.total_issues} inconsistência(s) encontrada(s)</span>}
              </div>
              {report && (
                <div className="text-[12px] text-[#A3A3A3] mt-1">
                  Financeiro: {report.summary.financial_divergences} · Contratos: {report.summary.contracts_issues} · Clientes: {report.summary.clients_issues} · Sócios: {report.summary.socios_divergences}
                </div>
              )}
            </div>
          </div>
          <button
            onClick={run}
            disabled={loading}
            className="fc-btn-gold flex items-center gap-2 px-5 py-2.5 self-start sm:self-auto"
            data-testid="btn-run-audit"
          >
            {loading
              ? <Loader2 size={16} className="animate-spin"/>
              : report
              ? <RefreshCcw size={16}/>
              : <ShieldCheck size={16}/>}
            {report ? "Re-executar auditoria" : "Executar Auditoria"}
          </button>
        </div>
        {report?.counts && (
          <div className="grid grid-cols-2 sm:grid-cols-7 gap-3 mt-5 pt-5 border-t border-[#1f1f1f] text-center">
            <CountTile label="Clientes" value={report.counts.clients_total}/>
            <CountTile label="Contratos" value={report.counts.contracts_total}/>
            <CountTile label="Ativos" value={report.counts.contracts_active}/>
            <CountTile label="Parcelados" value={report.counts.installments_active}/>
            <CountTile label="Sócios" value={report.counts.socios_total}/>
            <CountTile label="Sócios ativos" value={report.counts.socios_active}/>
            <CountTile label="Indicadores" value={report.counts.indicators_total}/>
          </div>
        )}
      </div>

      {report && (
        <div className="space-y-6">
          {/* CPF Duplicates — Investigação urgente */}
          <section className="fc-card p-5 sm:p-6 border-red-500/30">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-display text-[15px] font-semibold tracking-tight flex items-center gap-2">
                <AlertTriangle size={16} className="text-red-400"/> CPFs Duplicados — Diagnóstico
              </h2>
              <button onClick={runCpf} disabled={loadingCpf} className="fc-btn-ghost text-xs flex items-center gap-1.5" data-testid="btn-run-cpf">
                {loadingCpf ? <Loader2 size={12} className="animate-spin"/> : <RefreshCcw size={12}/>}
                {cpfReport ? "Re-executar" : "Buscar duplicatas"}
              </button>
            </div>
            {!cpfReport ? (
              <div className="text-[12.5px] text-[#737373]">Clique em &ldquo;Buscar duplicatas&rdquo; para listar todos os CPFs com múltiplos registros.</div>
            ) : (
              <>
                <div className="grid grid-cols-2 sm:grid-cols-6 gap-3 mb-4 text-center">
                  <CountTile label="CPFs duplicados" value={cpfReport.total_duplicated_cpfs}/>
                  <CountTile label="Registros duplicados" value={cpfReport.total_duplicate_records}/>
                  <CountTile label="Prováveis duplo-submit" value={cpfReport.likely_double_submit}/>
                  <CountTile label="Com contratos" value={cpfReport.cpfs_with_contracts}/>
                  <CountTile label="Com contratos ativos" value={cpfReport.cpfs_with_active_contracts}/>
                  <CountTile label="Validação backend" value={cpfReport.diagnostic.backend_cpf_check ? "✅" : "❌"}/>
                </div>
                <div className="rounded-md border border-red-500/30 bg-red-500/5 p-3 mb-4 text-[12px] text-red-100/90">
                  <strong className="text-red-200">Diagnóstico da causa raiz:</strong>
                  <ul className="list-disc pl-5 mt-1.5 space-y-0.5">
                    {cpfReport.diagnostic.notes.map((n, i) => <li key={i}>{n}</li>)}
                  </ul>
                </div>
                {cpfReport.duplicates.length === 0 ? (
                  <div className="text-emerald-300 text-[13px] flex items-center gap-2">
                    <CheckCircle2 size={14}/> Nenhuma duplicata encontrada
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-[12.5px]" data-testid="cpf-duplicates-table">
                      <thead>
                        <tr className="text-[10.5px] uppercase tracking-wider text-[#737373] border-b border-[#1f1f1f]">
                          <th className="text-left py-2 px-2">CPF</th>
                          <th className="text-right py-2 px-2">Qtd</th>
                          <th className="text-left py-2 px-2">IDs encontrados</th>
                          <th className="text-left py-2 px-2">Indicador</th>
                          <th className="text-left py-2 px-2">WhatsApp</th>
                          <th className="text-left py-2 px-2">Gap (s)</th>
                          <th className="text-center py-2 px-2">Contratos</th>
                          <th className="text-center py-2 px-2">Provável</th>
                        </tr>
                      </thead>
                      <tbody>
                        {cpfReport.duplicates.map((d) => (
                          <React.Fragment key={d.cpf}>
                            <tr className="border-b border-[#141414] bg-red-500/5">
                              <td className="py-2 px-2 font-mono-tab text-red-200">{d.cpf}</td>
                              <td className="py-2 px-2 text-right font-mono-tab text-red-300">{d.quantity}</td>
                              <td className="py-2 px-2 text-[10.5px] text-[#A3A3A3]">
                                {d.clients.map(c => c.id?.slice(0, 8)).join(" · ")}
                              </td>
                              <td className="py-2 px-2 text-[#A3A3A3]">{d.same_indicator ? "✓ mesmo" : "✗ diferente"}</td>
                              <td className="py-2 px-2 text-[#A3A3A3]">{d.same_whatsapp ? "✓ mesmo" : "✗ diferente"}</td>
                              <td className="py-2 px-2 font-mono-tab text-[#A3A3A3]">
                                {d.gap_seconds != null ? d.gap_seconds.toFixed(0) : "—"}
                              </td>
                              <td className="py-2 px-2 text-center">
                                {d.any_with_active_contracts
                                  ? <span className="badge-base bg-amber-500/15 border-amber-500/40 text-amber-200 text-[10px]">⚠ ATIVO</span>
                                  : d.any_with_contracts
                                  ? <span className="badge-base bg-[#1A1A1A] border-[#262626] text-[#A3A3A3] text-[10px]">com histórico</span>
                                  : <span className="text-emerald-300/70 text-[10px]">sem</span>}
                              </td>
                              <td className="py-2 px-2 text-center">
                                {d.likely_double_submit
                                  ? <span className="badge-base bg-red-500/15 border-red-500/40 text-red-200 text-[10px]">Duplo-submit</span>
                                  : <span className="text-[#737373] text-[10px]">—</span>}
                              </td>
                            </tr>
                            {d.clients.map((c) => (
                              <tr key={c.id} className="border-b border-[#0f0f0f] text-[11.5px]">
                                <td colSpan={2} className="py-1.5 px-2 pl-6 text-[#737373]">↳ {c.full_name}</td>
                                <td className="py-1.5 px-2 font-mono-tab text-[#A3A3A3]">{c.id}</td>
                                <td className="py-1.5 px-2 text-[#A3A3A3]">{c.indicator_name || "—"}</td>
                                <td className="py-1.5 px-2 text-[#A3A3A3]">{c.whatsapp || "—"}</td>
                                <td className="py-1.5 px-2 text-[#737373]">{c.created_at?.slice(0, 19).replace("T", " ")}</td>
                                <td className="py-1.5 px-2 text-center text-[11px]">
                                  {(c.contracts_total + c.ic_total) === 0
                                    ? <span className="text-[#737373]">—</span>
                                    : <span className={c.contracts_ativo + c.ic_ativo > 0 ? "text-amber-300" : "text-[#A3A3A3]"}>
                                        {c.contracts_total + c.ic_total} ({c.contracts_ativo + c.ic_ativo} ativo)
                                      </span>}
                                </td>
                                <td className="py-1.5 px-2 text-center text-[#737373]">{c.status}</td>
                              </tr>
                            ))}
                          </React.Fragment>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </>
            )}
          </section>

          {/* Financial */}
          <Section title="Auditoria Financeira" icon={TrendingUp} count={`${report.financial.filter(r=>r.ok).length}/${report.financial.length} OK`}>
            <div className="space-y-2">
              {report.financial.map((r, idx) => <FinancialRow key={idx} row={r}/>)}
            </div>
          </Section>

          {/* Rankings */}
          <Section title="Auditoria de Rankings" icon={TrendingUp}>
            <div className="grid lg:grid-cols-3 gap-4">
              <RankingCard title="Top 5 Indicadores" items={report.rankings.top_indicadores} format={i => `${i.name} — ${i.clientes} cliente(s)`}/>
              <RankingCard title="Top 5 Maiores Carteiras" items={report.rankings.top_carteiras} format={i => `${i.name} — ${formatBRL(i.carteira)}`}/>
              <RankingCard title="Top 5 Sem Atraso" items={report.rankings.top_sem_atraso} format={i => `${i.name} — ${i.total_clientes} cliente(s)`}/>
            </div>
            <div className="mt-3 text-[11px] text-[#737373]">
              Rankings calculados diretamente das collections raw. Compare visualmente com o Dashboard executivo.
            </div>
          </Section>

          {/* Contracts */}
          <Section
            title="Auditoria de Contratos"
            icon={FileText}
            accent={report.contracts_issues.length === 0 ? "green" : "red"}
            count={report.contracts_issues.length === 0 ? "✅ OK" : `${report.contracts_issues.length} problema(s)`}
          >
            {report.contracts_issues.length === 0 ? (
              <div className="text-[13px] text-emerald-300/90 flex items-center gap-2">
                <CheckCircle2 size={14}/> Nenhuma inconsistência detectada
              </div>
            ) : (
              <IssueList items={report.contracts_issues} idKey="contract_id"/>
            )}
          </Section>

          {/* Clients */}
          <Section
            title="Auditoria de Clientes"
            icon={Users}
            accent={report.clients_issues.length === 0 ? "green" : "red"}
            count={report.clients_issues.length === 0 ? "✅ OK" : `${report.clients_issues.length} problema(s)`}
          >
            {report.clients_issues.length === 0 ? (
              <div className="text-[13px] text-emerald-300/90 flex items-center gap-2">
                <CheckCircle2 size={14}/> Nenhuma inconsistência detectada
              </div>
            ) : (
              <IssueList items={report.clients_issues} idKey="client_id"/>
            )}
          </Section>

          {/* Sócios */}
          <Section title="Auditoria de Sócios" icon={Briefcase} count={`${report.socios.filter(s=>s.ok).length}/${report.socios.length} consistentes`}>
            <div className="overflow-x-auto">
              <table className="w-full text-[12.5px]">
                <thead>
                  <tr className="text-[10.5px] uppercase tracking-wider text-[#737373] border-b border-[#1f1f1f]">
                    <th className="text-left py-2 px-2">Sócio</th>
                    <th className="text-right py-2 px-2">Cap. Investido</th>
                    <th className="text-right py-2 px-2">Cap. Na Rua</th>
                    <th className="text-right py-2 px-2">Disponível</th>
                    <th className="text-right py-2 px-2">Juros Recebidos</th>
                    <th className="text-right py-2 px-2">LHS</th>
                    <th className="text-right py-2 px-2">RHS</th>
                    <th className="text-right py-2 px-2">Δ</th>
                    <th className="text-center py-2 px-2">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {report.socios.map(s => (
                    <tr key={s.id} className={`border-b border-[#141414] ${!s.ok ? "bg-red-500/5" : ""}`} data-testid={`socio-audit-${s.id}`}>
                      <td className="py-2 px-2">
                        {s.name}
                        {s.status !== "ativo" && <span className="ml-2 text-[10px] text-[#737373]">({s.status})</span>}
                      </td>
                      <td className="py-2 px-2 text-right font-mono-tab">{formatBRL(s.capital_invested)}</td>
                      <td className="py-2 px-2 text-right font-mono-tab">{formatBRL(s.capital_na_rua)}</td>
                      <td className="py-2 px-2 text-right font-mono-tab text-emerald-300">{formatBRL(s.capital_disponivel)}</td>
                      <td className="py-2 px-2 text-right font-mono-tab text-[#D4AF37]">{formatBRL(s.juros_recebidos)}</td>
                      <td className="py-2 px-2 text-right font-mono-tab text-[#A3A3A3]">{formatBRL(s.lhs)}</td>
                      <td className="py-2 px-2 text-right font-mono-tab text-[#A3A3A3]">{formatBRL(s.rhs)}</td>
                      <td className={`py-2 px-2 text-right font-mono-tab ${Math.abs(s.diff) < 0.05 ? "text-emerald-300" : "text-red-300"}`}>{formatBRL(s.diff)}</td>
                      <td className="py-2 px-2 text-center">
                        {s.ok
                          ? <CheckCircle2 size={16} className="text-emerald-400 inline"/>
                          : <XCircle size={16} className="text-red-400 inline"/>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="mt-3 text-[11px] text-[#737373]">
              <strong>Identidade contábil:</strong> LHS = Cap. Investido + Lucro Reinvestido + Recomposição Déficit · RHS = Cap. Na Rua + Disponível + Déficit
            </div>
          </Section>
        </div>
      )}
    </Layout>
  );
}

function CountTile({ label, value }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-[#737373]">{label}</div>
      <div className="font-mono-tab text-base mt-0.5">{value ?? "—"}</div>
    </div>
  );
}

function RankingCard({ title, items, format }) {
  return (
    <div className="rounded-md border border-[#1f1f1f] bg-[#0a0a0a] p-4">
      <div className="text-[11px] uppercase tracking-wider text-[#D4AF37] mb-2.5">{title}</div>
      {(!items || items.length === 0) ? (
        <div className="text-[12px] text-[#737373]">Sem dados</div>
      ) : (
        <ol className="space-y-1.5 text-[12.5px]">
          {items.map((it, idx) => (
            <li key={idx} className="flex items-center gap-2">
              <span className="text-[#737373] text-[11px] w-4">{idx + 1}.</span>
              <span className="flex-1 truncate">{format(it)}</span>
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}

function IssueList({ items, idKey }) {
  const grouped = {};
  for (const it of items) {
    grouped[it.kind] = (grouped[it.kind] || []).concat(it);
  }
  return (
    <div className="space-y-3">
      {Object.entries(grouped).map(([kind, list]) => (
        <details key={kind} className="rounded-md border border-red-500/20 bg-red-500/5 px-3 py-2.5">
          <summary className="cursor-pointer flex items-center gap-2 text-[13px]">
            <AlertTriangle size={14} className="text-red-400"/>
            <strong className="text-red-200">{ISSUE_LABEL[kind] || kind}</strong>
            <span className="text-[#A3A3A3]">— {list.length} ocorrência(s)</span>
          </summary>
          <ul className="mt-2.5 space-y-1.5 text-[12.5px] text-[#A3A3A3]">
            {list.slice(0, 50).map((it, i) => (
              <li key={i} className="pl-5">
                <span className="text-white">{it.detail}</span>
                {it[idKey] && <span className="text-[#737373] ml-2 font-mono-tab text-[11px]">#{String(it[idKey]).slice(0, 8)}</span>}
                {it.name && <span className="text-[#737373] ml-2">· {it.name}</span>}
                {it.cpf && <span className="text-[#737373] ml-2">CPF {it.cpf}</span>}
              </li>
            ))}
            {list.length > 50 && (
              <li className="pl-5 text-[#737373] text-[11px]">… +{list.length - 50} ocorrência(s)</li>
            )}
          </ul>
        </details>
      ))}
    </div>
  );
}
